import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { MuiThemeProvider } from '@material-ui/core/styles';
import { ThemeProvider } from 'styled-components';
import {
  applyThemeMode,
  initialMode,
  isMode,
  storeMode,
  themes,
} from '../../config/theme';

const ThemeModeContext = createContext({
  mode: initialMode,
  isDark: initialMode === 'dark',
  setMode: () => {},
  toggleMode: () => {},
});

// Holds the active mode and keeps three things in step with it: the CSS custom
// properties on the document root (which is what actually recolours the app), the
// Material UI theme, and the saved preference.
export const ThemeModeProvider = ({ children }) => {
  const [mode, setModeState] = useState(initialMode);

  const setMode = useCallback((next) => {
    if (!isMode(next)) return;
    setModeState((current) => {
      if (current === next) return current;
      applyThemeMode(next);
      storeMode(next);
      return next;
    });
  }, []);

  const toggleMode = useCallback(() => {
    setMode(mode === 'dark' ? 'light' : 'dark');
  }, [mode, setMode]);

  const value = useMemo(
    () => ({ mode, isDark: mode === 'dark', setMode, toggleMode }),
    [mode, setMode, toggleMode]
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <MuiThemeProvider theme={themes[mode]}>
        {/* The same theme object feeds both providers, so styled-components can
            read tokens with ({ theme }) => theme.colors.x */}
        <ThemeProvider theme={themes[mode]}>{children}</ThemeProvider>
      </MuiThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export const useThemeMode = () => useContext(ThemeModeContext);

export default useThemeMode;
